/** 12_INFORMATION_ARCHITECTURE §5. Web 과 Native 가 같은 경로를 해석한다 (IA-4). */
export const ROUTES = {
  home: "/",
  map: "/map",
  explore: "/explore",
  exploreCategory: (code: string) => `/explore/${code}`,
  search: "/search",
  place: (id: string) => `/places/${id}`,
  events: "/events",
  event: (id: string) => `/events/${id}`,
  passport: "/passport",
  passportStamps: "/passport/stamps",
  passportRegions: "/passport/regions",
  passportWorld: "/passport/world",
  passportWorldCountry: (code: string) => `/passport/world/${code}`,
  passportOshi: "/passport/oshi",
  passportTimeline: "/passport/timeline",
  passportCollection: "/passport/collection",
  me: "/me",
  meSaved: "/me/saved",
  meOshi: "/me/oshi",
  meReports: "/me/reports",
  meReport: (id: string) => `/me/reports/${id}`,
  meNotifications: "/me/notifications",
  meSettings: "/me/settings",
  meSettingsSection: (s: SettingsSection) => `/me/settings/${s}` as const,
  submit: "/submit",
  submitPlace: (id: string) => `/submit/place/${id}`,
  visitNew: "/visit/new",
  /*
   * ⚠️ 아래 셋은 **주소만 있고 화면이 없습니다.** 여는 화면이 없는 주소를
   * 표에 그냥 두면, 읽는 사람은 그 기능이 있다고 읽습니다. 지금 상태를 여기
   * 적어 둡니다.
   *
   *   profile     서버는 공개 여권을 내줍니다 (`/api/v1/passport/profile/<id>`).
   *               여는 화면이 없어서 "전체 공개" 설정에 갈 곳이 없습니다 (OD-037).
   *   share       무엇을 공유할지 정해지지 않았습니다. 공유 카드는 이미지라
   *               주소가 필요 없습니다.
   *   submitEvent 이벤트 지정 제보 화면이 없습니다. 장소 지정(`submitPlace`)만 있습니다.
   *
   * `ROUTES_WITHOUT_SCREEN` 이 이 목록이고, 라우터 테스트가 그 밖의 주소는
   * 전부 화면으로 이어지는지 확인합니다.
   */
  profile: (id: string) => `/u/${id}`,
  share: (id: string) => `/share/${id}`,
  submitEvent: (id: string) => `/submit/event/${id}`,
  legal: (doc: string) => `/legal/${doc}`,
} as const;

/** 주소는 정해졌지만 아직 여는 화면이 없는 것들. 위 주석에 이유가 있습니다. */
export const ROUTES_WITHOUT_SCREEN = ["profile", "share", "submitEvent"] as const;

/**
 * 소셜 로그인 (실측: backend/app/api/v1/auth/routes.py).
 * 별도 콜백 화면이 없다 — 제공자가 백엔드로 돌아오고, 백엔드가 브라우저를
 * `/?oauth=<provider>` 로 되돌린 뒤 쿠키 세션으로 토큰을 한 번만 내준다.
 * 그래서 프론트가 열어야 할 주소는 홈이고, 콜백 URL 은 백엔드 소유다.
 */
export const OAUTH_PROVIDERS = ["naver", "kakao", "google"] as const;
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number];

export const OAUTH = {
  /** 브라우저를 그대로 보낸다 (XHR 이 아니다 — 제공자 도메인으로 이동한다). */
  start: (p: OAuthProvider) => `/api/v1/auth/${p}/start`,
  /** 돌아온 뒤 토큰을 받아오는 곳. 한 번만 성공한다. */
  session: (p: OAuthProvider) => (p === "naver" ? "/auth/naver/session" : `/auth/oauth/session?provider=${p}`),
  returnParam: "oauth",
  errorParam: "oauthError",
} as const;

/** 설정 섹션은 자유 문자열이 아니다 — 리다이렉트 대상이 실재하는지 검증할 수 있어야 한다. */
export const SETTINGS_SECTIONS = ["notifications", "privacy", "location", "language", "account"] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

export const SETTINGS_ROUTES = SETTINGS_SECTIONS.map((s) => `/me/settings/${s}`);

/**
 * 탭도 화면이다 (IA-4). 탭이 URL 을 바꾸지 않으면 공유·뒤로가기·새로고침이
 * 모두 첫 탭으로 되돌아가고, `/passport/world` 같은 경로가 실제로는
 * 도달할 수 없는 주소가 된다.
 */
export const PASSPORT_SECTIONS = ["stamps", "regions", "world"] as const;
export type PassportSection = (typeof PASSPORT_SECTIONS)[number];

export const ME_SECTIONS = ["saved", "oshi", "reports", "notifications", "account"] as const;
export type MeSection = (typeof ME_SECTIONS)[number];

/**
 * Legacy URL 호환 (12 §5).
 * 대상은 실제로 그 내용을 보여주는 화면이어야 한다. `/passport/collection` 처럼
 * 아직 화면이 없는 주소로 보내면 주소창과 화면이 서로 다른 말을 하게 된다 —
 * 수집물은 스탬프 북이 곧 그 화면이다.
 */
export const LEGACY_REDIRECTS: Readonly<Record<string, string>> = {
  "/explore/global": "/explore",
  "/collection": ROUTES.passportStamps,
  "/passport/collection": ROUTES.passportStamps,
  "/passport/timeline": ROUTES.passportRegions,
  "/passport/oshi": ROUTES.meOshi,
  "/interests": ROUTES.meOshi,
  "/settings/language-region": "/me/settings/language",
};

export const BOTTOM_TABS = [
  { key: "home", route: ROUTES.home, label: "홈", icon: "home" },
  { key: "map", route: ROUTES.map, label: "지도", icon: "map" },
  { key: "explore", route: ROUTES.explore, label: "탐색", icon: "compass" },
  { key: "passport", route: ROUTES.passport, label: "여권", icon: "passport" },
  { key: "me", route: ROUTES.me, label: "MY", icon: "user" },
] as const;
